import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { CheckCircle, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      setLoading(false);
      toast.error('Payment status check timed out. Please check your dashboard.');
      return;
    }

    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`, {
        withCredentials: true,
      });

      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        setLoading(false);
        toast.success('Payment successful!');
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        setLoading(false);
        toast.error('Payment session expired.');
      } else {
        setTimeout(() => {
          setAttempts(attempts + 1);
          pollPaymentStatus();
        }, pollInterval);
      }
    } catch (error) {
      setPaymentStatus('error');
      setLoading(false);
      toast.error('Error checking payment status.');
    }
  };

  return (
    <div className="min-h-screen bg-surface-secondary" data-testid="payment-success-page">
      <Navbar />
      <div className="flex items-center justify-center min-h-screen pt-16 px-4">
        <div className="w-full max-w-md card text-center">
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
              <h2 className="font-manrope text-2xl font-bold text-primary mb-2">Processing Payment</h2>
              <p className="text-secondary">Please wait while we confirm your payment...</p>
            </>
          ) : paymentStatus === 'success' ? (
            <>
              <CheckCircle size={64} weight="duotone" className="text-primary mx-auto mb-4" data-testid="success-icon" />
              <h2 className="font-manrope text-2xl font-bold text-primary mb-2">Payment Successful!</h2>
              <p className="text-secondary mb-6">
                Your consultation has been activated. A specialist registered with the NMC or a State Medical Council will review your case and you will receive the expert medical opinion shortly.
              </p>
              <button
                onClick={() => navigate('/patient')}
                className="btn-primary px-8 py-3"
                data-testid="go-to-dashboard-button"
              >
                Go to Dashboard
              </button>
            </>
          ) : (
            <>
              <X size={64} weight="duotone" className="text-accent mx-auto mb-4" data-testid="error-icon" />
              <h2 className="font-manrope text-2xl font-bold text-primary mb-2">Payment Issue</h2>
              <p className="text-secondary mb-6">
                {paymentStatus === 'timeout'
                  ? 'Payment verification timed out. Please check your dashboard or contact support.'
                  : paymentStatus === 'expired'
                  ? 'Payment session has expired. Please try again.'
                  : 'There was an issue processing your payment. Please contact support.'}
              </p>
              <button
                onClick={() => navigate('/patient')}
                className="btn-primary px-8 py-3"
              >
                Go to Dashboard
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}